import { BlankSlate } from "@msuite/ui/blank-slate";
import { Droppable, getDroppablePayload } from "@msuite/ui/dnd";
import { useAutoAnimate } from "@msuite/ui/libs/auto-animate";
import { cn } from "@msuite/ui/utils";
import { guardArray } from "@msuite/utils/array";
import { generateId } from "@msuite/utils/strings";
import type { Tables, Vehicle } from "@msuite/utils/types";
import { mutators } from "@msuite/zero";
import { useZero } from "@msuite/zero/react";
import { syncHandler } from "@/api/sync-handler";
import { useDailyContext } from "./context";
import { DailyVehicleAssignmentPlan } from "./daily-vehicle-assignment-plan";

export function DailyVehicles() {
	/** Hooks */
	const { vehicleDisclosure, plan, planId, isInteractionBlocked } = useDailyContext();
	const [parent] = useAutoAnimate();
	const z = useZero();

	if (!vehicleDisclosure.isOpen) return null;

	// Plan-level vehicles exist on our own board only. The context also carries the reduced shape of
	// a partner's board — our crews on their plan — and their vehicles are not ours to list.
	const vehicleAssignmentsPlan =
		plan && "vehicleAssignmentsPlan" in plan ? plan.vehicleAssignmentsPlan : [];
	const hasAssignments = guardArray(vehicleAssignmentsPlan).length > 0;

	/** Render */
	return (
		<div ref={parent}>
			<Droppable
				accept={["vehicle", "vehicle-assignment"]}
				className={cn(
					"card bg-white/40 dark:bg-black/40 mb-4 grid grid-cols-3 gap-2 px-2 rounded-2xl",
					hasAssignments && "fat-stripes",
				)}
				handleOnDrop={(e) => {
					const payload = getDroppablePayload(e);
					if (payload.type === "vehicle") {
						const item = payload.item as Vehicle;
						syncHandler(
							z.mutate(
								mutators.vehicleAssignmentPlans.insert({
									id: generateId(),
									vehicleId: item.id,
									planId,
								}),
							),
						);
					} else if (payload.type === "vehicle-assignment") {
						const item = payload.item as Tables<"vehicleAssignments">;
						syncHandler(
							z.mutate(
								mutators.vehicleAssignmentPlans.insert({
									id: generateId(),
									vehicleId: item.vehicleId,
									planId,
								}),
							),
						);
					} else if (payload.type === "vehicle-assignment-plan") {
					}
				}}
				isDisabled={isInteractionBlocked}
			>
				{!hasAssignments && (
					<div className="col-span-3 w-full">
						<BlankSlate
							withAccessory={false}
							withBackground={false}
							withBorder={false}
						/>
					</div>
				)}
				{guardArray(vehicleAssignmentsPlan).map((vap) => (
					<DailyVehicleAssignmentPlan
						key={vap.id}
						vehicleAssignmentPlan={vap}
					/>
				))}
			</Droppable>
		</div>
	);
}
